// Egg relighting — runs in canvas space, after eggTransform has been baked
// into the rendered pixels, so the highlight stays where the author placed it
// regardless of how the egg is rotated/squished/scaled per-marc.
//
// Pipeline is split into three operations the caller composes:
//
//   computeNaturalEllipse — fit an axis-aligned ellipse to the raw egg PNG's
//                           alpha silhouette. Run once when the image loads
//                           and cache the result.
//
//   drawEggTransformed    — render the egg with eggTransform baked in
//                           (object-fit:contain inside frameW × frameH).
//                           Cheap; runs on every change so the egg moves
//                           live during slider drags.
//
//   applyLighting         — analytic per-pixel lighting using the cached
//                           natural ellipse + eggTransform. Rotated egg is
//                           inverse-rotated per pixel; non-uniform scale and
//                           translation likewise. Independent highlight,
//                           shadow, and size (= falloff power) parameters,
//                           so the author can tune brightening without
//                           dragging shadow along with it.
//
// Why analytic, not silhouette-fit: fitting an axis-aligned ellipse to the
// rendered silhouette of a rotated egg gives a bbox much larger than the
// egg, with the egg's tips at r²≈1 (no lighting). Worse, if the egg is
// partially clipped by the canvas the fit only sees the visible portion.
// Computing the ellipse from `naturalEllipse + eggTransform` sidesteps both.

const ALPHA_THRESHOLD = 8;

// Off-screen canvas used by computeNaturalEllipse. Module-scoped because
// re-creating one per call is wasteful; the egg PNG only changes when
// eggSrc changes (effectively never during a session).
let scratchCanvas = null;
function getScratchCtx(w, h) {
  if (!scratchCanvas) {
    scratchCanvas =
      typeof OffscreenCanvas !== "undefined"
        ? new OffscreenCanvas(w, h)
        : document.createElement("canvas");
  }
  scratchCanvas.width = w;
  scratchCanvas.height = h;
  return scratchCanvas.getContext("2d");
}

export function computeNaturalEllipse(img) {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const ctx = getScratchCtx(w, h);
  if (!ctx) return null;
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, w, h).data;
  let minX = w;
  let maxX = -1;
  let minY = h;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    const rowStart = y * w * 4;
    for (let x = 0; x < w; x++) {
      if (data[rowStart + x * 4 + 3] >= ALPHA_THRESHOLD) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const aBase = Math.max(1, (maxX - minX) / 2);
  const bBase = Math.max(1, (maxY - minY) / 2);

  // The bbox-inscribed ellipse touches the silhouette only at the four
  // bbox-extreme points. For an egg-shaped silhouette (wide end + narrow
  // end), the wide end's lateral bulge sits past the ellipse boundary —
  // those pixels end up with r² > 1 in lighting and get skipped, leaving
  // unlit slivers. Scan once to find the worst-offending pixel's r² and
  // scale the semi-axes uniformly so every opaque pixel lives inside.
  // Uniform scaling is approximate (axis-independent scaling could be
  // tighter in area) but it's stable and the over-cover is small in
  // practice: typical eggs need ~5–10% on top of the bbox fit.
  let maxR2 = 1;
  for (let y = minY; y <= maxY; y++) {
    const rowStart = y * w * 4;
    for (let x = minX; x <= maxX; x++) {
      if (data[rowStart + x * 4 + 3] >= ALPHA_THRESHOLD) {
        const u = (x - cx) / aBase;
        const v = (y - cy) / bBase;
        const r2 = u * u + v * v;
        if (r2 > maxR2) maxR2 = r2;
      }
    }
  }
  const k = Math.sqrt(maxR2);

  return {
    cx,
    cy,
    a: aBase * k,
    b: bBase * k,
    imgW: w,
    imgH: h,
  };
}

export function drawEggTransformed({
  destCtx,
  sourceImg,
  eggTransform,
  frameW,
  frameH,
}) {
  const {
    tx = 0,
    ty = 0,
    scale = 1,
    scaleX = 1,
    rotation = 0,
  } = eggTransform || {};
  const imgW = sourceImg.naturalWidth || sourceImg.width;
  const imgH = sourceImg.naturalHeight || sourceImg.height;
  const ratio = Math.min(frameW / imgW, frameH / imgH);
  const dw = imgW * ratio;
  const dh = imgH * ratio;
  const dx = (frameW - dw) / 2;
  const dy = (frameH - dh) / 2;

  destCtx.save();
  destCtx.clearRect(0, 0, frameW, frameH);
  destCtx.translate(frameW / 2 + tx, frameH / 2 + ty);
  destCtx.rotate((rotation * Math.PI) / 180);
  destCtx.scale(scale * scaleX, scale);
  destCtx.translate(-frameW / 2, -frameH / 2);
  destCtx.drawImage(sourceImg, dx, dy, dw, dh);
  destCtx.restore();
}

function normalizeLight(L) {
  const len = Math.sqrt(L.x * L.x + L.y * L.y + L.z * L.z);
  if (len < 1e-6) return { x: 0, y: 0, z: 1 };
  return { x: L.x / len, y: L.y / len, z: L.z / len };
}

// Map size (0..1, where 1 = broadest = Lambertian) to a power exponent.
// Exponential mapping so the slider feels perceptually uniform — each
// equal step roughly halves the highlight's angular size. size=1 →
// power=1 (Lambertian, broad gradient across the lit half); size=0 →
// power=2048 (very tight specular spot, ~2.5° cone around the lit pole) —
// sharp enough to read as a glint when stacked on top of a broader fill.
function sizeToPower(size) {
  if (size == null) return 1;
  const clamped = Math.max(0, Math.min(1, size));
  return Math.pow(2, (1 - clamped) * 11);
}

export function applyLighting({
  destCtx,
  naturalEllipse,
  eggTransform,
  frameW,
  frameH,
  lights,
}) {
  if (!naturalEllipse || !lights || lights.length === 0) return;
  // Pre-process the light list once: clamp + drop no-ops + cache pow exponent
  // and normalized direction so the per-pixel inner loop just reads numbers.
  const active = [];
  for (const L of lights) {
    if (!L) continue;
    const intensity = Math.max(0, Math.min(1, L.intensity ?? 0));
    const shadow = Math.max(0, Math.min(1, L.shadow ?? 0));
    if (intensity <= 0 && shadow <= 0) continue;
    active.push({
      intensity,
      shadow,
      power: sizeToPower(L.size),
      dir: normalizeLight(L.direction || { x: 0, y: 0, z: 1 }),
    });
  }
  if (active.length === 0) return;

  const {
    tx = 0,
    ty = 0,
    scale = 1,
    scaleX = 1,
    rotation = 0,
  } = eggTransform || {};
  const ratio = Math.min(
    frameW / naturalEllipse.imgW,
    frameH / naturalEllipse.imgH,
  );

  // Canvas-space ellipse parameters. Center derivation:
  //
  //   The egg image is contain-fit centered in the frame, so the IMAGE
  //   center maps to frame center. The SILHOUETTE center (which is what
  //   the natural ellipse is centered on) may sit at a different point
  //   within the image — often a few pixels off if the PNG isn't tightly
  //   cropped to the egg. We compute that drawing-space offset, then push
  //   it through the eggTransform (scale around frame center, rotate,
  //   translate by tx/ty) to land on the silhouette's canvas-space center.
  //
  // Semi-axes: natural a, b already absorb the contain-fit + per-marc
  // scale factors. Rotation around the silhouette center.
  const rad = (rotation * Math.PI) / 180;
  const cosR = Math.cos(rad);
  const sinR = Math.sin(rad);

  const offsetXDraw = (naturalEllipse.cx - naturalEllipse.imgW / 2) * ratio;
  const offsetYDraw = (naturalEllipse.cy - naturalEllipse.imgH / 2) * ratio;
  const sx = offsetXDraw * scale * scaleX;
  const sy = offsetYDraw * scale;
  const cx = frameW / 2 + tx + sx * cosR - sy * sinR;
  const cy = frameH / 2 + ty + sx * sinR + sy * cosR;
  const semiA = naturalEllipse.a * ratio * scale * scaleX;
  const semiB = naturalEllipse.b * ratio * scale;

  // Bounding box of the rotated ellipse in canvas-space, used to skip rows
  // entirely outside the egg. Tight for axis-aligned eggs; for rotated
  // eggs it's a slight over-estimate but still saves the bulk of the
  // background pixels from the inner-loop checks.
  const halfW = Math.abs(semiA * cosR) + Math.abs(semiB * sinR);
  const halfH = Math.abs(semiA * sinR) + Math.abs(semiB * cosR);
  const minX = Math.max(0, Math.floor(cx - halfW));
  const maxX = Math.min(frameW, Math.ceil(cx + halfW));
  const minY = Math.max(0, Math.floor(cy - halfH));
  const maxY = Math.min(frameH, Math.ceil(cy + halfH));

  const data = destCtx.getImageData(0, 0, frameW, frameH);
  const buf = data.data;

  for (let y = minY; y < maxY; y++) {
    const dyC = y - cy;
    const rowStart = y * frameW * 4;
    for (let x = minX; x < maxX; x++) {
      const i = rowStart + x * 4;
      // Skip transparent / off-egg pixels — analytic ellipse is an
      // approximation; alpha is the source of truth for "is this an egg
      // pixel."
      if (buf[i + 3] < ALPHA_THRESHOLD) continue;

      const dxC = x - cx;
      // Un-rotate (canvas → egg-aligned)
      const dxE = dxC * cosR + dyC * sinR;
      const dyE = -dxC * sinR + dyC * cosR;
      // Normalize against egg-aligned semi-axes
      const u = dxE / semiA;
      const v = dyE / semiB;
      const r2 = u * u + v * v;
      if (r2 >= 1) continue;
      const nz = Math.sqrt(1 - r2);

      // Normal in egg-aligned space, rotated back to canvas space.
      // (Skip the non-uniform-scale inverse-transpose — scaleX defaults to
      // ~0.92 so the orientation error is small and the simpler math is
      // worth it. Revisit if highlights look subtly wrong on heavily
      // squished eggs.)
      const nxE = u;
      const nyE = v;
      const nx = nxE * cosR - nyE * sinR;
      const ny = nxE * sinR + nyE * cosR;
      // (nx, ny, nz) is already unit-length since u² + v² + nz² = 1 and
      // rotation preserves length.

      // Sum each active light's contribution. Bright: Lambertian raised to
      // that light's size power — tight specular when size is small, broad
      // when size is 1. Only lights the lit hemisphere. Shadow: half-Lambert,
      // (1 - dot) / 2, peaking at the dark pole and falling smoothly to 0 at
      // the lit pole. Shadow stays diffuse regardless of size (size is
      // highlight tightness, not shadow tightness). Stacking lights is a
      // straight sum so a key + fill arrangement just composes — strongest
      // light dominates, weaker lights soften.
      let brightSum = 0;
      let darkSum = 0;
      for (let li = 0; li < active.length; li++) {
        const L = active[li];
        const dot = nx * L.dir.x + ny * L.dir.y + nz * L.dir.z;
        const bright = Math.pow(dot > 0 ? dot : 0, L.power);
        const dark = (1 - dot) / 2;
        brightSum += L.intensity * bright;
        darkSum += L.shadow * dark;
      }
      const factor = 1 + brightSum - darkSum;

      const r = buf[i] * factor;
      const g = buf[i + 1] * factor;
      const bch = buf[i + 2] * factor;
      buf[i] = r > 255 ? 255 : r < 0 ? 0 : r;
      buf[i + 1] = g > 255 ? 255 : g < 0 ? 0 : g;
      buf[i + 2] = bch > 255 ? 255 : bch < 0 ? 0 : bch;
    }
  }

  destCtx.putImageData(data, 0, 0);
}
